<template>
  <div class="filter-dropdown">
    <button
      ref="trigger"
      type="button"
      class="filter-trigger px-5 py-4" 
      :class="iconReverse ? 'flex-row-reverse' : ''"
      @click="toggle"
    >
      <Icon :icon-name="iconName"/>
      <span class="filter-trigger__label">{{ displayLabel }}</span>
    </button>

    <transition name="fade">
      <div
        v-if="isOpen"
        class="filter-menu"
        @click.stop
      >
        <div class="filter-search">
          <label :for="searchId" class="sr-only">{{ searchPlaceholder }}</label>
          <div class="filter-search__field">
            <Icon icon-name="SearchIcon" class="filter-search__icon" />
            <input
              :id="searchId"
              v-model="searchTerm"
              type="text"
              :placeholder="searchPlaceholder"
              class="filter-search__input"
            />
          </div>
        </div>

        <div class="filter-options">
          <div
            v-for="item in filteredItems"
            :key="item[itemKey] || item"
            @click="selectItem(item)"
            :class="['filter-option', { 'filter-option--selected': isSelected(item) }]"
          >
            <div
              v-if="showLeadingVisual && hasLeadingVisual(item)"
              class="filter-option__leading"
            >
              <img
                v-if="item.avatar"
                :src="item.avatar"
                :alt="`${item[titleField]} avatar`"
                class="filter-option__avatar"
              />
              <span
                v-else-if="item.initial && showFallbackInitial"
                class="filter-option__initial"
                :style="initialStyle(item)"
              >
                {{ item.initial }}
              </span>
              <Icon
                v-else-if="item.iconName"
                :icon-name="item.iconName"
                class="filter-option__icon"
              />
              <span
                v-else-if="showFallbackInitial"
                class="filter-option__letter"
              >
                {{ (item[titleField] || item).charAt(0) }}
              </span>
            </div>
            <div class="filter-option__text">
              <span class="filter-option__title">{{ item[titleField] || item }}</span>
              <span v-if="item[subtitleField]" class="filter-option__subtitle">{{ item[subtitleField] }}</span>
            </div>
          </div>
          <div v-if="filteredItems.length === 0" class="filter-empty">
            No results found
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Icon from './Icon.vue'

export default {
  name: "FilterDropdown",
  components: {
    Icon
  },
  props: {
    value: {
      type: Object,
      default: null
    },
    items: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: "Select"
    },
    iconName: {
      type: String,
      default: "OwnerIcon"
    },
    searchPlaceholder: {
      type: String,
      default: "Search..."
    },
    itemKey: {
      type: String,
      default: "id"
    },
    titleField: {
      type: String,
      default: "title"
    },
    subtitleField: {
      type: String,
      default: "subtitle"
    },
    showFallbackInitial: {
      type: Boolean,
      default: true
    },
    showLeadingVisual: {
      type: Boolean,
      default: true
    },
    iconReverse: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      searchTerm: "",
      currentSelection: this.value ? { ...this.value } : null,
      searchId: `filter-dropdown-search-${Math.random().toString(36).slice(2)}`
    };
  },
  computed: {
    displayLabel() {
      if (this.currentSelection) {
        return this.currentSelection[this.titleField] || this.label;
      }
      return this.label;
    },
    filteredItems() {
      if (!this.searchTerm) return this.items;
      const term = this.searchTerm.toLowerCase();
      return this.items.filter((item) => {
        const title = (item[this.titleField] || item || "").toString().toLowerCase();
        const subtitle = (item[this.subtitleField] || "").toString().toLowerCase();
        return title.includes(term) || subtitle.includes(term);
      });
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.currentSelection = newVal ? { ...newVal } : null;
      },
      deep: true
    }
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.searchTerm = "";
        document.addEventListener("click", this.onOutsideClick);
      } else {
        document.removeEventListener("click", this.onOutsideClick);
      }
    },
    onOutsideClick(event) {
      if (!this.$refs.trigger) return;
      if (this.$el.contains(event.target)) return;
      this.close();
    },
    close() {
      this.isOpen = false;
      document.removeEventListener("click", this.onOutsideClick);
    },
    selectItem(item) {
      this.currentSelection = { ...item };
      this.$emit("input", this.currentSelection);
      this.$emit("select", this.currentSelection);
      this.close();
    },
    isSelected(item) {
      if (!this.currentSelection) return false;
      const currentKey = this.currentSelection[this.itemKey] || this.currentSelection;
      const itemKey = item[this.itemKey] || item;
      return currentKey === itemKey;
    },
    hasLeadingVisual(item) {
      if (item.avatar || item.iconName) return true;
      if (item.initial && this.showFallbackInitial) return true;
      if (!this.showFallbackInitial) return false;
      const title = item[this.titleField] || item;
      return typeof title === "string" && title.length > 0;
    },
    initialStyle(item) {
      const color = item.initialColor;
      if (typeof color === "string" && color.trim()) {
        if (color.startsWith("#") || color.startsWith("rgb")) {
          return { backgroundColor: color };
        }
      }
      return {};
    }
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onOutsideClick);
  }
};
</script>

<style scoped>
.filter-dropdown {
  position: relative;
  display: inline-block;
}
.filter-trigger {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #0398dc;
  border-radius: 4px;
  background-color: #ffffff;
  color: #0398dc;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.filter-trigger:focus {
  outline: none;
  box-shadow: none;
}
.filter-trigger:hover {
  background-color: #e5f5fd;
}
.filter-trigger__label {
  text-align: left;
}
.filter-menu {
  position: absolute;
  right: 0;
  margin-top: 8px;
  z-index: 30;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  box-shadow: 0 20px 35px rgba(15, 23, 42, 0.08);
}
.filter-search {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
}
.filter-search__field {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background-color: #ffffff;
  padding: 0 12px;
}
.filter-search__icon {
  width: 16px;
  height: 16px;
  color: #94a3b8;
}
.filter-search__input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px 0 8px 8px;
  font-size: 13px;
  color: #334155;
}
.filter-search__input::placeholder {
  color: #94a3b8;
}
.filter-search__input:focus {
  outline: none;
}
.filter-options {
  max-height: 288px;
  overflow-y: auto;
}
.filter-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  font-size: 13px;
  color: #334155;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.filter-option:hover {
  background-color: #f1f5f9;
}
.filter-option--selected {
  background-color: #d9eefc;
  color: #0398dc;
  font-weight: 600;
}
.filter-option__leading {
  min-width: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background-color: #e2e8f0;
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
}
.filter-option__avatar {
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  object-fit: cover;
}
.filter-option__initial {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  background-color: #0398dc;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
}
.filter-option__icon {
  width: 18px;
  height: 18px;
  color: #64748b;
}
.filter-option__letter {
  color: #0398dc;
  font-weight: 700;
  font-size: 14px;
}
.filter-option__text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.filter-option__title {
  font-weight: 600;
  color: inherit;
}
.filter-option__subtitle {
  font-size: 12px;
  color: #64748b;
}
.filter-empty {
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: #94a3b8;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>